// ========================================
// LINKEDIN OAUTH SERVICE
// ========================================
import { db, auth } from '../../firebase';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { oauthConfig, generateOAuthState } from '../../config/oauth.config';

export interface LinkedInAuthResponse {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  error?: string;
}

export interface LinkedInProfile {
  id: string;
  firstName: string;
  lastName: string;
  profilePicture?: string;
  email?: string;
}

class LinkedInOAuthService {
  private readonly config = oauthConfig.linkedin;
  
  /**
   * Step 1: Start OAuth flow - Redirect user to LinkedIn authorization
   */
  startOAuthFlow(): void {
    const state = generateOAuthState();
    
    console.log('🔐 Starting LinkedIn OAuth flow...');
    console.log('  - Generated state:', state);
    console.log('  - Storing in sessionStorage...');
    
    // Store state in sessionStorage for verification in callback
    sessionStorage.setItem('linkedin_oauth_state', state);
    sessionStorage.setItem('linkedin_oauth_timestamp', Date.now().toString());
    
    // Verify it was stored
    const storedState = sessionStorage.getItem('linkedin_oauth_state');
    console.log('  - Verification - stored state:', storedState);
    console.log('  - Match:', storedState === state);
    
    // Build authorization URL
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.config.clientId,
      redirect_uri: this.config.redirectUri,
      state: state,
      scope: this.config.scopes.join(' ')
    });
    
    const authUrl = `${this.config.authUrl}?${params.toString()}`;
    
    console.log('📍 Redirect URI:', this.config.redirectUri);
    console.log('🔑 Client ID being used:', this.config.clientId);
    console.log('🔍 Full OAuth URL:', authUrl);
    console.log('🚀 Redirecting to LinkedIn...');
    
    // Redirect to LinkedIn
    window.location.href = authUrl;
  }
  
  /**
   * Step 2: Handle OAuth callback and exchange code for access token
   */
  async handleCallback(code: string, state: string): Promise<LinkedInAuthResponse> {
    try {
      // Verify state to prevent CSRF attacks
      const storedState = sessionStorage.getItem('linkedin_oauth_state');
      const timestamp = sessionStorage.getItem('linkedin_oauth_timestamp');
      
      console.log('🔐 State validation:');
      console.log('  - Received state:', state);
      console.log('  - Stored state:', storedState);
      console.log('  - Match:', storedState === state);
      
      if (!storedState) {
        console.warn('⚠️ WARNING: sessionStorage was cleared during OAuth redirect!');
        console.warn('⚠️ This could be due to:');
        console.warn('   - Browser privacy settings');
        console.warn('   - Incognito/Private mode');
        console.warn('   - Third-party cookie blocking');
        console.warn('⚠️ Proceeding WITHOUT state validation (TEMPORARY - for debugging only)');
        // TODO: Fix this by using a more persistent storage mechanism
      } else if (storedState !== state) {
        console.error('❌ State mismatch! Received:', state, 'Stored:', storedState);
        throw new Error('Invalid state parameter - mogelijk CSRF aanval');
      }
      
      // Check if state is not too old (max 10 minutes)
      if (timestamp) {
        const age = Date.now() - parseInt(timestamp);
        if (age > 10 * 60 * 1000) {
          throw new Error('OAuth state verlopen - probeer opnieuw');
        }
      }
      
      // Clear state from storage
      sessionStorage.removeItem('linkedin_oauth_state');
      sessionStorage.removeItem('linkedin_oauth_timestamp');
      
      console.log('✅ State verified, exchanging code for token...');
      
      // Exchange authorization code for access token
      const tokenResponse = await this.exchangeCodeForToken(code);
      
      if (!tokenResponse.success || !tokenResponse.accessToken) {
        throw new Error(tokenResponse.error || 'Failed to get access token');
      }
      
      console.log('✅ Access token received');
      
      // Profile and tokens are already stored on backend by the Cloud Function
      // We just need to update the UI connection status in Firestore
      await this.updateConnectionStatus(true);
      
      console.log('✅ LinkedIn account successfully connected!');
      
      return tokenResponse;
      
    } catch (error) {
      console.error('❌ LinkedIn OAuth error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Exchange authorization code for access token (for login flow)
   * SECURE: Uses Firebase Cloud Function to create user and get custom token
   */
  private async exchangeCodeForLogin(code: string): Promise<{ success: boolean; customToken?: string; profile?: LinkedInProfile; error?: string }> {
    try {
      console.log('🔐 LinkedIn login - trying existing Cloud Function');
      
      // Try the existing linkedinLogin Cloud Function
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const linkedinLoginFn = httpsCallable(functions, 'linkedinLogin');
      
      console.log('📞 Calling linkedinLogin function with code:', code.substring(0, 20) + '...');
      
      const result = await linkedinLoginFn({
        code: code,
        redirectUri: this.config.redirectUri,
      });
      
      console.log('📥 Function response:', result);
      
      const data = result.data as any;
      
      if (!data.success || !data.customToken) {
        console.error('❌ Function returned error:', data.error);
        throw new Error(data.error || 'Login failed');
      }
      
      console.log('✅ Custom token received, signing in...');
      
      // Sign in with custom token
      const { signInWithCustomToken } = await import('firebase/auth');
      await signInWithCustomToken(auth, data.customToken);
      
      console.log('✅ Firebase authentication successful');
      
      const profile = data.profile as LinkedInProfile;
      console.log('✅ LinkedIn profile:', profile);

      // Step 3: Create/sign in Firebase user with email
      const { signInWithEmailAndPassword, createUserWithEmailAndPassword, updateProfile } = await import('firebase/auth');
      
      if (!profile.email) {
        throw new Error('LinkedIn profile does not have email');
      }

      // Try to sign in first
      let firebaseUser;
      try {
        console.log('🔐 Attempting to sign in with email:', profile.email);
        const userCredential = await signInWithEmailAndPassword(auth, profile.email, `linkedin_${profile.id}`);
        firebaseUser = userCredential.user;
        console.log('✅ Signed in existing user');
      } catch (signInError: any) {
        // If user doesn't exist, create account
        if (signInError.code === 'auth/user-not-found' || signInError.code === 'auth/wrong-password') {
          console.log('👤 Creating new Firebase user...');
          const userCredential = await createUserWithEmailAndPassword(auth, profile.email, `linkedin_${profile.id}`);
          firebaseUser = userCredential.user;
          
          // Update profile with LinkedIn name
          await updateProfile(firebaseUser, {
            displayName: `${profile.firstName} ${profile.lastName}`,
            photoURL: profile.profilePicture
          });
          console.log('✅ New user created');
        } else {
          throw signInError;
        }
      }

      console.log('✅ Firebase authentication successful');
      
      return {
        success: true,
        profile: profile
      };
      
    } catch (error) {
      console.error('LinkedIn login error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed'
      };
    }
  }

  /**
   * Exchange authorization code for access token
   * SECURE: Uses Firebase Cloud Function to keep client_secret on server
   */
  private async exchangeCodeForToken(code: string): Promise<LinkedInAuthResponse> {
    try {
      // Check if this is a login flow
      const isLoginFlow = sessionStorage.getItem('linkedinLoginFlow') === 'true';
      
      if (isLoginFlow) {
        // Use login function that creates/signs in user
        const loginResult = await this.exchangeCodeForLogin(code);
        
        if (loginResult.success) {
          return {
            success: true,
            accessToken: 'stored_on_backend',
            expiresIn: 3600 // Default
          };
        } else {
          throw new Error(loginResult.error || 'Login failed');
        }
      }
      
      // Regular OAuth flow for social sharing
      // Use Firebase Cloud Function for secure token exchange
      console.log('🔐 Exchanging code for token via Cloud Function...');
      console.log('📦 Code length:', code?.length);
      
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      console.log('✅ Firebase Functions loaded');
      
      const linkedinTokenExchangeFn = httpsCallable(functions, 'linkedinTokenExchange', {
        timeout: 30000 // 30 second timeout
      });
      console.log('✅ Cloud Function reference created');
      
      // IMPORTANT: Use the EXACT same redirect URI that was used to start the OAuth flow
      // This must match what LinkedIn received in the authorization request
      // Use the same redirect URI from config (dynamically determined based on environment)
      const redirectUri = this.config.redirectUri;
      console.log('📍 Using redirect URI for token exchange:', redirectUri);
      console.log('🚀 Calling Cloud Function NOW...');
      
      const result = await linkedinTokenExchangeFn({
        code: code,
        redirectUri: redirectUri,
      });
      
      console.log('📥 Cloud Function response received:', result);
      
      const data = result.data as any;
      
      if (!data.success) {
        throw new Error(data.error || 'Token exchange failed');
      }
      
      console.log('✅ Token exchange successful via Cloud Function');
      console.log('✅ LinkedIn profile fetched:', data.profile);
      console.log('✅ Token automatically stored by Cloud Function:', data.message);
      
      // Token is already stored by Cloud Function, but verify it exists
      // Wait a moment for Firestore write to complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Verify token was stored
      const verifyDoc = await getDoc(doc(db, 'socialConnections', auth.currentUser!.uid));
      const savedData = verifyDoc.data();
      
      if (!savedData?.linkedin?.accessToken) {
        console.warn('⚠️ Token not found immediately after Cloud Function call');
        // Token might still be writing, Cloud Function should handle it
      } else {
        console.log('✅ Token verified in Firestore, length:', savedData.linkedin.accessToken.length);
      }
      
      return {
        success: true,
        accessToken: data.accessToken, // Returned for verification
        expiresIn: data.expiresIn
      };
      
    } catch (error) {
      console.error('❌ Token exchange error:', error);
      throw error;
    }
  }
  
  // getUserProfile is no longer needed - backend handles this
  
  /**
   * Save LinkedIn connection details to Firestore
   * ⚠️ SECURITY: Access token is stored for posting functionality
   * In production, consider encrypting tokens or using a more secure storage method
   */
  private async saveLinkedInConnection(accessToken: string, profile: LinkedInProfile, expiresIn: number): Promise<void> {
    console.log('💾 saveLinkedInConnection called');
    console.log('🔑 Access Token length:', accessToken?.length);
    console.log('👤 Profile:', profile);
    console.log('⏰ Expires in:', expiresIn, 'seconds');
    
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }
    
    const userId = auth.currentUser.uid;
    const now = new Date();
    const expiresAt = new Date(now.getTime() + expiresIn * 1000);
    
    console.log('👤 User ID:', userId);
    console.log('📅 Expires at:', expiresAt);
    
    try {
      const connectionRef = doc(db, 'socialConnections', userId);
      const connectionDoc = await getDoc(connectionRef);
      
      const linkedinData = {
        connected: true,
        connectedAt: now,
        username: `${profile.firstName} ${profile.lastName}`,
        profileUrl: `https://www.linkedin.com/in/${profile.id}`,
        expiresAt: expiresAt,
        profileId: profile.id,
        profilePicture: profile.profilePicture,
        // Store access token for posting (needed by Cloud Function)
        accessToken: accessToken
      };
      
      console.log('📦 LinkedIn data to save:', {
        ...linkedinData,
        accessToken: accessToken ? `${accessToken.substring(0, 20)}... (${accessToken.length} chars)` : 'MISSING!'
      });
      
      if (connectionDoc.exists()) {
        // Update existing document
        console.log('📝 Updating existing document...');
        await updateDoc(connectionRef, {
          linkedin: linkedinData,
          updatedAt: now
        });
        console.log('✅ Document updated');
      } else {
        // Create new document
        console.log('📝 Creating new document...');
        await setDoc(connectionRef, {
          uid: userId,
          linkedin: linkedinData,
          facebook: { connected: false },
          instagram: { connected: false },
          twitter: { connected: false },
          createdAt: now,
          updatedAt: now
        });
        console.log('✅ Document created');
      }
      
      // Verify it was saved
      const verifyDoc = await getDoc(connectionRef);
      const savedData = verifyDoc.data();
      console.log('🔍 VERIFICATION - Saved accessToken exists:', !!savedData?.linkedin?.accessToken);
      console.log('🔍 VERIFICATION - Saved accessToken length:', savedData?.linkedin?.accessToken?.length);
      
      console.log('✅ LinkedIn connection saved to Firestore (with access token)');
      
    } catch (error) {
      console.error('❌ Error saving LinkedIn connection:', error);
      throw error;
    }
  }
  
  /**
   * Update connection status in Firestore (UI only, tokens are on backend)
   * IMPORTANT: This function MERGES with existing data, does NOT overwrite
   */
  private async updateConnectionStatus(connected: boolean): Promise<void> {
    if (!auth.currentUser) {
      throw new Error('User not authenticated');
    }
    
    const userId = auth.currentUser.uid;
    const now = new Date();
    
    try {
      const connectionRef = doc(db, 'socialConnections', userId);
      const connectionDoc = await getDoc(connectionRef);
      
      console.log('📝 Updating connection status. Connected:', connected);
      
      if (connectionDoc.exists()) {
        // Get existing LinkedIn data
        const existingLinkedInData = connectionDoc.data().linkedin || {};
        console.log('📄 Existing LinkedIn data:', {
          ...existingLinkedInData,
          accessToken: existingLinkedInData.accessToken ? `(${existingLinkedInData.accessToken.length} chars)` : 'NONE'
        });
        
        // MERGE: Keep all existing data, only update specific fields
        const updatedFields: any = {
          'linkedin.connected': connected,
          'updatedAt': now
        };
        
        // Only add timestamps if they have values (Firestore doesn't accept undefined)
        if (connected) {
          updatedFields['linkedin.connectedAt'] = now;
        } else {
          updatedFields['linkedin.disconnectedAt'] = now;
        }
        
        console.log('📝 Updating fields (MERGE mode):', Object.keys(updatedFields));
        
        // Update using dot notation to MERGE instead of overwrite
        await updateDoc(connectionRef, updatedFields);
        
        console.log('✅ Connection status updated (existing data preserved)');
        
        // VERIFY the token is still there after update
        const verifyDoc = await getDoc(connectionRef);
        const verifyData = verifyDoc.data();
        console.log('🔍 VERIFICATION after update:');
        console.log('  - connected:', verifyData?.linkedin?.connected);
        console.log('  - accessToken exists:', !!verifyData?.linkedin?.accessToken);
        console.log('  - accessToken length:', verifyData?.linkedin?.accessToken?.length || 0);
        
      } else {
        // Create new document (should not happen in normal OAuth flow, Cloud Function creates it first)
        console.warn('⚠️ Document does not exist, creating new one (this should not happen in normal flow)');
        await setDoc(connectionRef, {
          uid: userId,
          linkedin: {
            connected: connected,
            connectedAt: connected ? now : undefined,
            disconnectedAt: !connected ? now : undefined
          },
          facebook: { connected: false },
          instagram: { connected: false },
          twitter: { connected: false },
          createdAt: now,
          updatedAt: now
        });
        console.log('✅ New document created');
      }
      
    } catch (error) {
      console.error('❌ Error updating connection status:', error);
      throw error;
    }
  }
  
  /**
   * Post content to LinkedIn
   * SECURE: Uses Cloud Function with backend-stored tokens
   */
  async postToLinkedIn(
    content: string, 
    userId: string, 
    organizationId?: string,
    imageUrls?: string[]
  ): Promise<{ success: boolean; shareUrl?: string; error?: string }> {
    try {
      // If no organizationId provided, check if user has a default organization ID set
      let finalOrgId = organizationId;
      if (!finalOrgId) {
        const { getDoc, doc: firestoreDoc } = await import('firebase/firestore');
        const connectionDoc = await getDoc(firestoreDoc(db, 'socialConnections', userId));
        const linkedinData = connectionDoc.data()?.linkedin;
        finalOrgId = linkedinData?.postAsOrganization ? linkedinData?.defaultOrganizationId : undefined;
      }
      
      console.log('🏢 LinkedIn posting config:', {
        organizationId: finalOrgId,
        postType: finalOrgId ? 'Organization' : 'Personal',
        imageCount: imageUrls?.length || 0
      });
      
      // Import Firebase Functions
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      
      // Call secure backend function
      const postToLinkedInFn = httpsCallable(functions, 'postToLinkedIn');
      
      const result = await postToLinkedInFn({
        content: content,
        organizationId: finalOrgId, // Optional: undefined for personal, ID for organization
        imageUrls: imageUrls || [] // Optional: images to upload
      });
      
      const data = result.data as {
        success: boolean;
        shareUrl?: string;
        message?: string;
        error?: string;
      };
      
      if (!data.success) {
        throw new Error(data.error || data.message || 'Failed to post to LinkedIn');
      }
      
      console.log('✅ Posted to LinkedIn successfully via Cloud Function:', data.shareUrl);
      
      return {
        success: true,
        shareUrl: data.shareUrl
      };
      
    } catch (error) {
      console.error('❌ LinkedIn posting error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Disconnect LinkedIn account
   * SECURE: Uses Cloud Function to revoke tokens on backend
   */
  async disconnect(userId: string): Promise<void> {
    console.log('🔌 LinkedIn disconnect starting for userId:', userId);
    
    try {
      // Update Firestore connection status
      const { updateDoc, doc: firestoreDoc } = await import('firebase/firestore');
      const connectionRef = firestoreDoc(db, 'socialConnections', userId);
      
      console.log('📝 Updating Firestore connection document...');
      await updateDoc(connectionRef, {
        'linkedin.connected': false,
        'linkedin.disconnectedAt': new Date(),
        'linkedin.accessToken': null // Clear token
      });
      
      console.log('✅ LinkedIn disconnected from Firestore');
      
      // Optional: Call backend to revoke tokens (if Cloud Function exists)
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const revokeTokens = httpsCallable(functions, 'revokeLinkedInTokens');
        await revokeTokens({});
        console.log('✅ LinkedIn tokens revoked via Cloud Function');
      } catch (fnError) {
        console.log('ℹ️ Cloud Function not available or failed (this is OK):', fnError);
      }
      
      console.log('✅ LinkedIn disconnected successfully');
    } catch (error) {
      console.error('❌ Error disconnecting LinkedIn:', error);
      throw error;
    }
  }
  
  /**
   * Check if LinkedIn is connected
   */
  async isConnected(userId: string): Promise<boolean> {
    try {
      const connectionRef = doc(db, 'socialConnections', userId);
      const connectionDoc = await getDoc(connectionRef);
      
      if (!connectionDoc.exists()) return false;
      
      const linkedinData = connectionDoc.data().linkedin;
      
      // Simple check: is connected flag set?
      // Token expiry is managed on backend
      return linkedinData?.connected === true;
      
    } catch (error) {
      console.error('Error checking LinkedIn connection:', error);
      return false;
    }
  }
}

export const linkedinOAuth = new LinkedInOAuthService();

